import {Navigate, RouteObject} from 'react-router-dom';
import {lazy} from 'react';

import {PATHS} from './paths';

const Dashboard = lazy(() => import('../pages/dashboard/DashboardPage'));
const FeatureToggles = lazy(
  () => import('../pages/feature-toggles/FeatureTogglesPage'),
);
const FeatureToggleDetail = lazy(
  () => import('../pages/feature-toggles/FeatureToggleDetailPage'),
);
const Users = lazy(() => import('../pages/users/UsersPage'));
const UserCreate = lazy(() => import('../pages/users/UserCreatePage'));
const UserDetail = lazy(() => import('../pages/users/UserDetailPage'));
const UserEdit = lazy(() => import('../pages/users/UserEditPage'));
const Roles = lazy(() => import('../pages/roles/RolesPage'));
const RoleDetail = lazy(() => import('../pages/roles/RoleDetailPage'));
const RoleCreate = lazy(() => import('../pages/roles/CreateRoleDrawer'));
const RoleEdit = lazy(() => import('../pages/roles/EditRoleDrawer'));
const BulkActions = lazy(() => import('../pages/bulk-actions/BulkActionsPage'));
const DevTools = lazy(() => import('../pages/dev-tools/DevToolsPage'));
const UnprovisionedEbicsAccounts = lazy(
  () =>
    import('../pages/unprovisioned-ebics-accounts/UnprovisionedEbicsAccountsPage'),
);
const UnallocatedPayments = lazy(
  () => import('../pages/unallocated-payments/UnallocatedPaymentsPage'),
);
const UnallocatedOutgoingPayments = lazy(
  () =>
    import('../pages/unallocated-outgoing-payments/UnallocatedOutgoingPaymentsPage'),
);
const UnallocatedOutgoingPaymentDetail = lazy(
  () =>
    import('../pages/unallocated-outgoing-payments/UnallocatedOutgoingPaymentDetailPage'),
);
const AllocatePayment = lazy(
  () => import('../pages/unallocated-outgoing-payments/AllocatePaymentDrawer'),
);
const ManagerBackgroundProcesses = lazy(
  () =>
    import('../pages/manager-background-processes/ManagerBackgroundProcessesPage'),
);
const ManagerBackgroundProcessDetail = lazy(
  () =>
    import('../pages/manager-background-processes/ManagerBackgroundProcessDetailPage'),
);
const BankCreditTransfers = lazy(
  () => import('../pages/bank-credit-transfers/BankCreditTransfersPage'),
);
const BankTransactions = lazy(
  () => import('../pages/bank-transactions/BankTransactionsPage'),
);
const BankTransactionCreate = lazy(
  () => import('../pages/bank-transactions/BankTransactionCreateDrawer'),
);
const IntradayBankTransactions = lazy(
  () =>
    import('../pages/intraday-bank-transactions/IntradayBankTransactionsPage'),
);
const BankTransactionDetail = lazy(
  () => import('../pages/bank-transactions/BankTransactionDetailPage'),
);
const BankAccountStatements = lazy(
  () => import('../pages/bank-account-statements/BankAccountStatementsPage'),
);
const BankAccountStatementDetail = lazy(
  () =>
    import('../pages/bank-account-statements/BankAccountStatementDetailPage'),
);
const BankCreditTransferDetail = lazy(
  () => import('../pages/bank-credit-transfers/BankCreditTransferDetailPage'),
);
const BankDirectDebits = lazy(
  () => import('../pages/bank-direct-debits/BankDirectDebitsPage'),
);
const BankDirectDebitDetail = lazy(
  () => import('../pages/bank-direct-debits/BankDirectDebitDetailPage'),
);
const EverrealEvents = lazy(
  () => import('../pages/everreal-events/EverrealEventsPage'),
);
const EverrealEventDetail = lazy(
  () => import('../pages/everreal-events/EverrealEventDetailPage'),
);
const InactiveEmailRecipients = lazy(
  () =>
    import('../pages/inactive-email-recipients/InactiveEmailRecipientsPage'),
);
const ImpowerEvents = lazy(
  () => import('../pages/impower-events/ImpowerEventsPage'),
);
const ImpowerEventDetail = lazy(
  () => import('../pages/impower-events/ImpowerEventDetailPage'),
);
const VvrbEvents = lazy(() => import('../pages/vvrb-events/VvrbEventsPage'));
const VvrbEventDetail = lazy(
  () => import('../pages/vvrb-events/VvrbEventDetailPage'),
);
const VvrbRequests = lazy(
  () => import('../pages/vvrb-requests/VvrbRequestsPage'),
);
const VvrbRequestDetail = lazy(
  () => import('../pages/vvrb-requests/VvrbRequestDetailPage'),
);
const BankAccountVibanProjections = lazy(
  () =>
    import('../pages/bank-account-viban-projections/BankAccountVibanProjectionsPage'),
);
const BankAccountVibanProjectionDetail = lazy(
  () =>
    import('../pages/bank-account-viban-projections/BankAccountVibanProjectionDetailPage'),
);
const PaymentFiles = lazy(
  () => import('../pages/payment-files/PaymentFilesPage'),
);
const PaymentFileDetail = lazy(
  () => import('../pages/payment-files/PaymentFileDetailPage'),
);
const DepositAccountVibans = lazy(
  () => import('../pages/deposit-account-vibans/DepositAccountVibansPage'),
);
const DepositAccountVibanDetail = lazy(
  () => import('../pages/deposit-account-vibans/DepositAccountVibanDetailPage'),
);
const EditDepositAccountViban = lazy(
  () => import('../pages/deposit-account-vibans/EditDepositAccountVibanDrawer'),
);
const Deposits = lazy(() => import('../pages/deposits/DepositsPage'));
const DepositDetail = lazy(() => import('../pages/deposits/DepositDetailPage'));
const DepositGuaranteeApplications = lazy(
  () =>
    import('../pages/deposit-guarantee-applications/DepositGuaranteeApplicationsPage'),
);
const DepositGuaranteeApplicationDetail = lazy(
  () =>
    import('../pages/deposit-guarantee-applications/DepositGuaranteeApplicationDetailPage'),
);
const EditDepositGuaranteeApplication = lazy(
  () =>
    import('../pages/deposit-guarantee-applications/EditDepositGuaranteeApplicationDrawer'),
);
const Claims = lazy(() => import('../pages/claims/ClaimsPage'));
const ClaimDetail = lazy(() => import('../pages/claims/ClaimDetailPage'));
const PropertyOwners = lazy(
  () => import('../pages/property-owners/PropertyOwnersPage'),
);
const PropertyOwnerDetail = lazy(
  () => import('../pages/property-owners/PropertyOwnerDetailPage'),
);
const PropertyOwnerCreate = lazy(
  () => import('../pages/property-owners/PropertyOwnerCreatePage'),
);
const PropertyOwnerEdit = lazy(
  () => import('../pages/property-owners/EditPropertyOwnerDrawer'),
);
const RentalContracts = lazy(
  () => import('../pages/rental-contracts/RentalContractsPage'),
);
const RentalContractDetail = lazy(
  () => import('../pages/rental-contracts/RentalContractDetailPage'),
);
const RentalContractEdit = lazy(
  () => import('../pages/rental-contracts/EditRentalContractDrawer'),
);
const Condominiums = lazy(
  () => import('../pages/condominiums/CondominiumsPage'),
);
const CondominiumCreate = lazy(
  () => import('../pages/condominiums/CondominiumCreatePage'),
);
const CondominiumDetail = lazy(
  () => import('../pages/condominiums/CondominiumDetailPage'),
);
const CondominiumEdit = lazy(
  () => import('../pages/condominiums/CondominiumEditPage'),
);
const Accounts = lazy(() => import('../pages/accounts/AccountsPage'));
const AccountDetail = lazy(() => import('../pages/accounts/AccountDetailPage'));
const AccountCreate = lazy(
  () => import('../pages/accounts/AccountCreateDrawer'),
);
const AccountEdit = lazy(() => import('../pages/accounts/AccountEditDrawer'));
const Managers = lazy(() => import('../pages/managers/ManagersPage'));
const ManagerCreate = lazy(() => import('../pages/managers/ManagerCreatePage'));
const ManagerDetail = lazy(() => import('../pages/managers/ManagerDetailPage'));
const ManagerEdit = lazy(() => import('../pages/managers/ManagerEditPage'));
const Organisations = lazy(
  () => import('../pages/organisations/OrganisationsPage'),
);
const OrganisationCreate = lazy(
  () => import('../pages/organisations/CreateOrganisationDrawer'),
);
const OrganisationDetail = lazy(
  () => import('../pages/organisations/OrganisationDetailPage'),
);
const OrganisationEdit = lazy(
  () => import('../pages/organisations/EditOrganisationDrawer'),
);
const Account = lazy(() => import('../pages/account/AccountPage'));
const NotFound = lazy(() => import('../pages/NotFound'));
const UiShowcase = lazy(() => import('../pages/ui-showcase/UiShowcasePage'));
const UiButtons = lazy(
  () => import('../pages/ui-showcase/sections/ButtonsSection'),
);
const UiInputs = lazy(
  () => import('../pages/ui-showcase/sections/InputsSection'),
);
const UiFormFields = lazy(
  () => import('../pages/ui-showcase/sections/FormFieldsSection'),
);
const UiDataDisplay = lazy(
  () => import('../pages/ui-showcase/sections/DataDisplaySection'),
);
const UiDialogs = lazy(
  () => import('../pages/ui-showcase/sections/DialogsSection'),
);
const UiFeedback = lazy(
  () => import('../pages/ui-showcase/sections/FeedbackSection'),
);
const UiTypography = lazy(
  () => import('../pages/ui-showcase/sections/TypographySection'),
);
const UiLayout = lazy(
  () => import('../pages/ui-showcase/sections/LayoutSection'),
);
const UiSpacing = lazy(
  () => import('../pages/ui-showcase/sections/SpacingSection'),
);
const UiColors = lazy(
  () => import('../pages/ui-showcase/sections/ColorsSection'),
);
const UiBorderRadius = lazy(
  () => import('../pages/ui-showcase/sections/BorderRadiusSection'),
);
const UiShadows = lazy(
  () => import('../pages/ui-showcase/sections/ShadowsSection'),
);
const UiBreakpoints = lazy(
  () => import('../pages/ui-showcase/sections/BreakpointsSection'),
);
const UiIcons = lazy(
  () => import('../pages/ui-showcase/sections/IconsSection'),
);

export type AppRoute = RouteObject & {
  meta?: {
    title?: string;
    requiresAuth?: boolean;
    parentPath?: string;
    parentLabel?: string;
  };
};

export const routes: AppRoute[] = [
  {
    path: '/',
    element: <Navigate to={PATHS.dashboard.to} replace />,
  },
  {
    path: PATHS.dashboard.pattern,
    meta: {title: 'Dashboard', requiresAuth: true},
    element: <Dashboard />,
  },
  {
    path: PATHS.managers.pattern,
    meta: {title: 'Managers', requiresAuth: true},
    element: <Managers />,
    children: [{path: 'new', element: <ManagerCreate />}],
  },
  {
    path: PATHS.manager_detail.pattern,
    meta: {
      title: 'Manager',
      requiresAuth: true,
      parentPath: PATHS.managers.to,
      parentLabel: 'Managers',
    },
    element: <ManagerDetail />,
    children: [{path: 'edit', element: <ManagerEdit />}],
  },
  {
    path: PATHS.organisations.pattern,
    meta: {title: 'Organisations', requiresAuth: true},
    element: <Organisations />,
    children: [{path: 'new', element: <OrganisationCreate />}],
  },
  {
    path: PATHS.organisation_detail.pattern,
    meta: {
      title: 'Organisation',
      requiresAuth: true,
      parentPath: PATHS.organisations.to,
      parentLabel: 'Organisations',
    },
    element: <OrganisationDetail />,
    children: [{path: 'edit', element: <OrganisationEdit />}],
  },
  {
    path: PATHS.feature_toggles.pattern,
    meta: {title: 'Feature Toggles', requiresAuth: true},
    element: <FeatureToggles />,
  },
  {
    path: PATHS.feature_toggle_detail.pattern,
    meta: {
      title: 'Feature Toggle',
      requiresAuth: true,
      parentPath: PATHS.feature_toggles.to,
      parentLabel: 'Feature Toggles',
    },
    element: <FeatureToggleDetail />,
  },
  {
    path: PATHS.users.pattern,
    meta: {title: 'Users', requiresAuth: true},
    element: <Users />,
    children: [{path: 'new-backoffice-user', element: <UserCreate />}],
  },
  {
    path: PATHS.user_detail.pattern,
    meta: {
      title: 'User',
      requiresAuth: true,
      parentPath: PATHS.users.to,
      parentLabel: 'Users',
    },
    element: <UserDetail />,
    children: [{path: 'edit', element: <UserEdit />}],
  },
  {
    path: PATHS.roles.pattern,
    meta: {title: 'Roles', requiresAuth: true},
    element: <Roles />,
    children: [{path: 'new', element: <RoleCreate />}],
  },
  {
    path: PATHS.role_detail.pattern,
    meta: {
      title: 'Role',
      requiresAuth: true,
      parentPath: PATHS.roles.to,
      parentLabel: 'Roles',
    },
    element: <RoleDetail />,
    children: [{path: 'edit', element: <RoleEdit />}],
  },
  {
    path: PATHS.unallocated_payments.pattern,
    meta: {title: 'Unallocated Payments', requiresAuth: true},
    element: <UnallocatedPayments />,
  },
  {
    path: PATHS.unallocated_outgoing_payments.pattern,
    meta: {title: 'Unallocated Outgoing Payments', requiresAuth: true},
    element: <UnallocatedOutgoingPayments />,
  },
  {
    path: PATHS.unprovisioned_ebics_accounts.pattern,
    meta: {title: 'Unprovisioned Accounts', requiresAuth: true},
    element: <UnprovisionedEbicsAccounts />,
  },
  {
    path: PATHS.unallocated_outgoing_payment_detail.pattern,
    meta: {
      title: 'Unallocated Outgoing Payment',
      requiresAuth: true,
      parentPath: PATHS.unallocated_outgoing_payments.to,
      parentLabel: 'Unallocated Outgoing Payments',
    },
    element: <UnallocatedOutgoingPaymentDetail />,
    children: [{path: 'allocate', element: <AllocatePayment />}],
  },
  {
    path: PATHS.manager_background_processes.pattern,
    meta: {title: 'Manager Background Processes', requiresAuth: true},
    element: <ManagerBackgroundProcesses />,
  },
  {
    path: PATHS.manager_background_process_detail.pattern,
    meta: {
      title: 'Manager Background Process',
      requiresAuth: true,
      parentPath: PATHS.manager_background_processes.to,
      parentLabel: 'Manager Background Processes',
    },
    element: <ManagerBackgroundProcessDetail />,
  },
  {
    path: PATHS.bulk_actions.pattern,
    meta: {title: 'Bulk Actions', requiresAuth: true},
    element: <BulkActions />,
  },
  ...(import.meta.env.DEV
    ? [
        {
          path: PATHS.dev_tools.pattern,
          meta: {title: 'Dev Tools', requiresAuth: true},
          element: <DevTools />,
        },
      ]
    : []),
  {
    path: PATHS.bank_account_viban_projections.pattern,
    meta: {title: 'Bank Account vIBAN Projections', requiresAuth: true},
    element: <BankAccountVibanProjections />,
  },
  {
    path: PATHS.bank_account_viban_projection_detail.pattern,
    meta: {
      title: 'Bank Account vIBAN Projection',
      requiresAuth: true,
      parentPath: PATHS.bank_account_viban_projections.to,
      parentLabel: 'Bank Account vIBAN Projections',
    },
    element: <BankAccountVibanProjectionDetail />,
  },
  {
    path: PATHS.payment_files.pattern,
    meta: {title: 'Payment Files', requiresAuth: true},
    element: <PaymentFiles />,
  },
  {
    path: PATHS.payment_file_detail.pattern,
    meta: {
      title: 'Payment File',
      requiresAuth: true,
      parentPath: PATHS.payment_files.to,
      parentLabel: 'Payment Files',
    },
    element: <PaymentFileDetail />,
  },
  {
    path: PATHS.deposit_account_vibans.pattern,
    meta: {title: 'Deposit Account vIBANs', requiresAuth: true},
    element: <DepositAccountVibans />,
  },
  {
    path: PATHS.deposit_account_viban_detail.pattern,
    meta: {
      title: 'Deposit Account VIBAN',
      requiresAuth: true,
      parentPath: PATHS.deposit_account_vibans.to,
      parentLabel: 'Deposit Account vIBANs',
    },
    element: <DepositAccountVibanDetail />,
    children: [{path: 'edit', element: <EditDepositAccountViban />}],
  },
  {
    path: PATHS.deposits.pattern,
    meta: {title: 'Deposits', requiresAuth: true},
    element: <Deposits />,
  },
  {
    path: PATHS.deposit_detail.pattern,
    meta: {
      title: 'Deposit',
      requiresAuth: true,
      parentPath: PATHS.deposits.to,
      parentLabel: 'Deposits',
    },
    element: <DepositDetail />,
  },
  {
    path: PATHS.deposit_guarantee_applications.pattern,
    meta: {
      title: 'Deposit Guarantee Applications',
      requiresAuth: true,
    },
    element: <DepositGuaranteeApplications />,
  },
  {
    path: PATHS.deposit_guarantee_application_detail.pattern,
    meta: {
      title: 'Deposit Guarantee Application',
      requiresAuth: true,
      parentPath: PATHS.deposit_guarantee_applications.to,
      parentLabel: 'Deposit Guarantee Applications',
    },
    element: <DepositGuaranteeApplicationDetail />,
    children: [{path: 'edit', element: <EditDepositGuaranteeApplication />}],
  },
  {
    path: PATHS.claims.pattern,
    meta: {title: 'Claims', requiresAuth: true},
    element: <Claims />,
  },
  {
    path: PATHS.claim_detail.pattern,
    meta: {
      title: 'Claim',
      requiresAuth: true,
      parentPath: PATHS.claims.to,
      parentLabel: 'Claims',
    },
    element: <ClaimDetail />,
  },
  {
    path: PATHS.property_owners.pattern,
    meta: {title: 'Property Owners', requiresAuth: true},
    element: <PropertyOwners />,
    children: [{path: 'new', element: <PropertyOwnerCreate />}],
  },
  {
    path: PATHS.property_owner_detail.pattern,
    meta: {
      title: 'Property Owner',
      requiresAuth: true,
      parentPath: PATHS.property_owners.to,
      parentLabel: 'Property Owners',
    },
    element: <PropertyOwnerDetail />,
    children: [{path: 'edit', element: <PropertyOwnerEdit />}],
  },
  {
    path: PATHS.rental_contracts.pattern,
    meta: {title: 'Rental Contracts', requiresAuth: true},
    element: <RentalContracts />,
  },
  {
    path: PATHS.rental_contract_detail.pattern,
    meta: {
      title: 'Rental Contract',
      requiresAuth: true,
      parentPath: PATHS.rental_contracts.to,
      parentLabel: 'Rental Contracts',
    },
    element: <RentalContractDetail />,
    children: [{path: 'edit', element: <RentalContractEdit />}],
  },
  {
    path: PATHS.condominiums.pattern,
    meta: {title: 'Condominiums', requiresAuth: true},
    element: <Condominiums />,
    children: [{path: 'new', element: <CondominiumCreate />}],
  },
  {
    path: PATHS.condominium_detail.pattern,
    meta: {
      title: 'Condominium',
      requiresAuth: true,
      parentPath: PATHS.condominiums.to,
      parentLabel: 'Condominiums',
    },
    element: <CondominiumDetail />,
    children: [{path: 'edit', element: <CondominiumEdit />}],
  },
  {
    path: PATHS.accounts.pattern,
    meta: {title: 'Accounts', requiresAuth: true},
    element: <Accounts />,
    children: [{path: 'new', element: <AccountCreate />}],
  },
  {
    path: PATHS.account_detail.pattern,
    meta: {
      title: 'Account',
      requiresAuth: true,
      parentPath: PATHS.accounts.to,
      parentLabel: 'Accounts',
    },
    element: <AccountDetail />,
    children: [{path: 'edit', element: <AccountEdit />}],
  },
  {
    path: PATHS.bank_credit_transfers.pattern,
    meta: {title: 'Bank Credit Transfers', requiresAuth: true},
    element: <BankCreditTransfers />,
  },
  {
    path: PATHS.bank_credit_transfer_detail.pattern,
    meta: {
      title: 'Bank Credit Transfer',
      requiresAuth: true,
      parentPath: PATHS.bank_credit_transfers.to,
      parentLabel: 'Bank Credit Transfers',
    },
    element: <BankCreditTransferDetail />,
  },
  {
    path: PATHS.bank_direct_debits.pattern,
    meta: {title: 'Bank Direct Debits', requiresAuth: true},
    element: <BankDirectDebits />,
  },
  {
    path: PATHS.bank_direct_debit_detail.pattern,
    meta: {
      title: 'Bank Direct Debit',
      requiresAuth: true,
      parentPath: PATHS.bank_direct_debits.to,
      parentLabel: 'Bank Direct Debits',
    },
    element: <BankDirectDebitDetail />,
  },
  {
    path: PATHS.bank_transactions.pattern,
    meta: {title: 'Bank Transactions', requiresAuth: true},
    element: <BankTransactions />,
    children: [{path: 'new', element: <BankTransactionCreate />}],
  },
  {
    path: PATHS.intraday_bank_transactions.pattern,
    meta: {title: 'Intraday Transactions', requiresAuth: true},
    element: <IntradayBankTransactions />,
  },
  {
    path: PATHS.bank_transaction_detail.pattern,
    meta: {
      title: 'Bank Transaction',
      requiresAuth: true,
      parentPath: PATHS.bank_transactions.to,
      parentLabel: 'Bank Transactions',
    },
    element: <BankTransactionDetail />,
  },
  {
    path: PATHS.bank_account_statements.pattern,
    meta: {title: 'Bank Account Statements', requiresAuth: true},
    element: <BankAccountStatements />,
  },
  {
    path: PATHS.bank_account_statement_detail.pattern,
    meta: {
      title: 'Bank Account Statement',
      requiresAuth: true,
      parentPath: PATHS.bank_account_statements.to,
      parentLabel: 'Bank Account Statements',
    },
    element: <BankAccountStatementDetail />,
  },
  {
    path: PATHS.everreal_events.pattern,
    meta: {title: 'EverReal Events', requiresAuth: true},
    element: <EverrealEvents />,
  },
  {
    path: PATHS.everreal_event_detail.pattern,
    meta: {
      title: 'EverReal Event',
      requiresAuth: true,
      parentPath: PATHS.everreal_events.to,
      parentLabel: 'EverReal Events',
    },
    element: <EverrealEventDetail />,
  },
  {
    path: PATHS.impower_events.pattern,
    meta: {title: 'impower Events', requiresAuth: true},
    element: <ImpowerEvents />,
  },
  {
    path: PATHS.inactive_email_recipients.pattern,
    meta: {title: 'Inactive Email Recipients', requiresAuth: true},
    element: <InactiveEmailRecipients />,
  },
  {
    path: PATHS.impower_event_detail.pattern,
    meta: {
      title: 'impower Event',
      requiresAuth: true,
      parentPath: PATHS.impower_events.to,
      parentLabel: 'impower Events',
    },
    element: <ImpowerEventDetail />,
  },
  {
    path: PATHS.vvrb_events.pattern,
    meta: {title: 'VVRB Events', requiresAuth: true},
    element: <VvrbEvents />,
  },
  {
    path: PATHS.vvrb_event_detail.pattern,
    meta: {
      title: 'VVRB Event',
      requiresAuth: true,
      parentPath: PATHS.vvrb_events.to,
      parentLabel: 'VVRB Events',
    },
    element: <VvrbEventDetail />,
  },
  {
    path: PATHS.vvrb_requests.pattern,
    meta: {title: 'VVRB Requests', requiresAuth: true},
    element: <VvrbRequests />,
  },
  {
    path: PATHS.vvrb_request_detail.pattern,
    meta: {
      title: 'VVRB Request',
      requiresAuth: true,
      parentPath: PATHS.vvrb_requests.to,
      parentLabel: 'VVRB Requests',
    },
    element: <VvrbRequestDetail />,
  },
  {
    path: PATHS.ui_showcase.pattern,
    meta: {title: 'UI Showcase', requiresAuth: true},
    element: <UiShowcase />,
    children: [
      {index: true, element: <Navigate to="colors" replace />},
      {path: 'colors', element: <UiColors />},
      {path: 'buttons', element: <UiButtons />},
      {path: 'inputs', element: <UiInputs />},
      {path: 'form-fields', element: <UiFormFields />},
      {path: 'data-display', element: <UiDataDisplay />},
      {path: 'dialogs', element: <UiDialogs />},
      {path: 'feedback', element: <UiFeedback />},
      {path: 'typography', element: <UiTypography />},
      {path: 'layout', element: <UiLayout />},
      {path: 'spacing', element: <UiSpacing />},
      {path: 'border-radius', element: <UiBorderRadius />},
      {path: 'shadows', element: <UiShadows />},
      {path: 'breakpoints', element: <UiBreakpoints />},
      {path: 'icons', element: <UiIcons />},
    ],
  },
  {
    path: PATHS.account.pattern,
    meta: {title: 'Account', requiresAuth: true},
    element: <Account />,
  },
  {
    path: '*',
    meta: {title: 'Not Found'},
    element: <NotFound />,
  },
];
